import SeoManager from '@/components/SeoManager';
import { localStorageKeys, modalTypeKey, ORDER_STATUS } from '@/configs/constants';
import useLocalStorage from '@/hooks/useLocalStorage';
import useRouteChange from '@/hooks/useRouteChange';
import useUrlParams from '@/hooks/useUrlParams';
import client from '@/lib/graphql/apolloClient';
import english from '@/lib/messages/langs/en.json';
import { wrapper } from '@/redux/store';
import '@/styles/_main.scss';
import loadMessages, { debugLog } from '@/utils';
import { ApolloProvider } from '@apollo/client';
import { NextIntlClientProvider } from 'next-intl';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import OneSignal from 'react-onesignal';
import { Provider } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../components/PhoneNumberSelect/phoneNumberSelect.scss';
import { LoginTypeProvider } from '@/context/LoginTypeContext';
import { currentRegion } from '@/lib/region';
const Loader = dynamic(() => import('@/components/loader'), {
  ssr: false
});
const RegionGate = dynamic(() => import('@/components/RegionGate'), {
  ssr: false
});
const ErrorBoundary = dynamic(() => import('@/components/ErrorBoundary'));
const AuthGuard = dynamic(() => import('@/components/Layout/AuthGuard'));
const MainLayout = dynamic(() => import('@/components/Layout/MainLayout'));
const ModalWrapper = dynamic(() => import('@/components/ModalWrapper'));
export default function App({
  Component,
  ...rest
}) {
  const {
    props,
    store
  } = wrapper.useWrappedStore(rest);
  const router = useRouter();
  const {
    locale,
    pathname,
    asPath,
    defaultLocale
  } = router;
  const [mounted, setMounted] = useState(false);
  const {
    addMultipleParams
  } = useUrlParams();
  const {
    actions
  } = useLocalStorage('orderListenStatus');
  const {
    actions: authActions
  } = useLocalStorage(localStorageKeys.AUTH_DATA);
  const [userId, setUserId] = useState(null);
  const [messages, setMessages] = useState({});
  const [oneSignalReady, setOneSignalReady] = useState(false);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const userId = authActions.getNestedValue('userId');
      setUserId(userId);
    }
  }, [pathname]);
  useEffect(() => {
    if (pathname === '/order') return;
    const paymentStatus = actions.getNestedValue('status');
    debugLog({
      message: 'paymentStatus',
      params: paymentStatus
    });
    if (paymentStatus === ORDER_STATUS.COMPLETED) {
      actions.updateNestedValue('status', '');
      addMultipleParams({
        [modalTypeKey]: 'selectOrderType',
        clearAddress: true
      });
    }
  }, [asPath]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useRouteChange(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  });
  const initOneSignal = useCallback(async () => {
    if (typeof window === 'undefined') return;
    try {
      await OneSignal.init({
        appId: process.env.NEXT_PUBLIC_ONESIGNAL_APP_ID,
        notifyButton: {
          enable: true
        },
        allowLocalhostAsSecureOrigin: true
      });
      setOneSignalReady(true);
    } catch (error) {
      console.error('OneSignal initialization failed:', error);
      setOneSignalReady(false);
    }
  }, []);
  const loginOneSignal = useCallback(async () => {
    if (typeof window === 'undefined' || !userId || !oneSignalReady) return;
    if (OneSignal.User?.externalId === userId) return;
    try {
      await OneSignal.login(userId);
      OneSignal.User.addTags({
        UserType: 'CUSTOMER'
      });
      if (!OneSignal.Notifications.permission) {
        OneSignal.Notifications.requestPermission();
      }
    } catch (error) {
      console.error('OneSignal login failed:', error);
    }
  }, [userId, oneSignalReady]);
  useEffect(() => {
    setMounted(true);
    initOneSignal();
  }, [initOneSignal]);
  useEffect(() => {
    if (userId && oneSignalReady) {
      loginOneSignal();
    }
  }, [userId, oneSignalReady, loginOneSignal]);
  useEffect(() => {
    const loadMessagesData = async () => {
      try {
        const messages = await loadMessages(locale);
        setMessages(messages);
      } catch (error) {
        console.error('Failed to load messages', error);
      }
    };
    loadMessagesData();
  }, [locale]);
  if (!messages) {
    return;
  }
  const withProviders = children => <NextIntlClientProvider messages={messages || {}} locale={locale || defaultLocale} now={new Date()} onError={err => {
    console.error('Intl Error:', {
      ...err
    });
  }} defaultMessages={english} data-sentry-element="NextIntlClientProvider" data-sentry-component="withProviders" data-sentry-source-file="_app.jsx">
            <ApolloProvider client={client} data-sentry-element="ApolloProvider" data-sentry-source-file="_app.jsx">
                <Provider store={store} data-sentry-element="Provider" data-sentry-source-file="_app.jsx">{children}</Provider>
            </ApolloProvider>
        </NextIntlClientProvider>;
  const pageTitle = 'Chicking';
  const is404 = pathname === '/404';
  const renderContent = () => {
    if (!mounted) {
      return <div className="min-h-screen flex items-center justify-center">
                    <Loader />
                </div>;
    }

    // No valid selected country → no backend region: show only the country picker, so nothing regional
    // (profile, cart, orders, menus) loads from a wrong region. Picking a country reloads into its region.
    if (!currentRegion && !is404) {
      return <>
                    <ToastContainer position="top-right" autoClose={3000} hideProgressBar={true} icon={false} closeOnClick pauseOnHover rtl={locale === 'ar'} />
                    <RegionGate />
                </>;
    }
    if (Component.requireAuth) {
      return <AuthGuard>
                    <PageWrapper>
                        <Component {...props.pageProps} />
                    </PageWrapper>
                </AuthGuard>;
    }
    if (is404) {
      return <Component {...props.pageProps} />;
    }
    return <ErrorBoundary data-sentry-element="ErrorBoundary" data-sentry-component="renderContent" data-sentry-source-file="_app.jsx">
                <PageWrapper data-sentry-element="PageWrapper" data-sentry-source-file="_app.jsx">
                    <Component {...props.pageProps} data-sentry-element="Component" data-sentry-source-file="_app.jsx" />
                </PageWrapper>
            </ErrorBoundary>;
  };
  return withProviders(<>
            <Head>
                <title>{pageTitle}</title>
                <link rel="preconnect" href="https://fonts.googleapis.com"></link>
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous"></link>
            </Head>
            {renderContent()}
        </>);
}
App.propTypes = {
  Component: PropTypes.elementType.isRequired,
  pageProps: PropTypes.object.isRequired
};
function PageWrapper({
  children
}) {
  const router = useRouter();
  return <>
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={true} icon={false} closeOnClick pauseOnHover rtl={router.locale === 'ar'} data-sentry-element="ToastContainer" data-sentry-source-file="_app.jsx" />
            <SeoManager data-sentry-element="SeoManager" data-sentry-source-file="_app.jsx" />
            <Loader data-sentry-element="Loader" data-sentry-source-file="_app.jsx" />
            <ModalWrapper data-sentry-element="ModalWrapper" data-sentry-source-file="_app.jsx" />
            <MainLayout data-sentry-element="MainLayout" data-sentry-source-file="_app.jsx">
                <LoginTypeProvider data-sentry-element="LoginTypeProvider" data-sentry-source-file="_app.jsx">{children}</LoginTypeProvider>
            </MainLayout>
        </>;
}
PageWrapper.propTypes = {
  children: PropTypes.node.isRequired
};